import * as React from "react";
import querystring from "querystring";
import { withRouter } from "react-router-dom";
import styled, { css } from "styled-components";

import Gettext, { withGettext } from "../../common/Gettext";
import { withFetch } from "../../common/fetch";
import { fontSize } from "../../common/styled-mixins";

import {
  LOGIN_TAB_ID,
  REGISTER_TAB_ID,
  PASSWORD_RESTORE_TAB_ID,
} from "../Auth";
import Tab from "../Tab";
import Message from "../Message";
import ErrorMessage from "../ErrorMessage";
import SocialLink from "../SocialLink";
import BtnFormSubmit from "../BtnFormSubmit";
import Input from "../Input";

import Container from "../StyledComponents/Container";
import Link from "../StyledComponents/FakeLink";
import validators from "../validators";
import loginWith from "../loginWith";
import { gaReducer } from "../../GA";
import { submitAppsFlyerParameters } from "../../Scorpion";

const isIronForm =
  process.env.REACT_APP_DEPLOY_TARGET === "irontrade" ||
  process.env.REACT_APP_DEPLOY_TARGET === "eztrader";

const TabContainer = styled.div`
  width: 100%;
  margin-bottom: auto;
  font-size: 0;
  line-height: 0;
  vertical-align: bottom;
  border-bottom: 2px solid ${({ theme }) => theme.accent};
`;

const SubmitContainer = styled.div`
  direction: ${(props) => props.theme.direction};
  position: relative;
  text-align: justify;
  vertical-align: middle;
  padding: 11px 0;

  ${(props) =>
    !props.showSocial &&
    css`
      text-align: center !important;
    `};

  ${isIronForm &&
    css`
      @media (max-width: 374px) {
        height: auto;
        text-align: center;
      }
    `};

  :after {
    content: "";
    display: inline-block;
    width: 100%;
  }

  ${({ theme }) =>
    theme.newIndex &&
    css`
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      &::after {
        display: none;
      }
    `};
`;

const FormText = styled.p`
  padding: 0 20px;
  padding-bottom: 9px;
  margin: 0;
  font-size: 21px;
  font-weight: 300;
  line-height: 34px;
  color: #fff;

  @media (max-width: 360px) {
    font-size: 20px;
    line-height: 28px;
  }

  ${({ theme }) =>
    theme.newIndex &&
    css`
      font-weight: 700;
      font-size: ${fontSize(18)};
      line-height: 24px;
      color: #ffffff;
      text-align: center;
    `};
`;

const Text = styled.span`
  margin: 0 10px;
  display: inline-block;
  vertical-align: middle;
  font-size: ${fontSize(12)};
  line-height: 1.2;
  text-transform: uppercase;
  color: #fff;

  @media (max-width: 374px) {
    margin: 0 5px;
  }

  ${({ theme }) =>
    theme.newIndex &&
    css`
      color: #ffffff;
      text-transform: lowercase;
    `};
`;

const ForgotContainer = styled.div`
  padding-top: 10px;
  ${({ theme }) =>
    theme.newIndex &&
    css`
      text-align: center;
    `};
`;

const ForgotLink = styled(Link)`
  display: inline-block;
  font-size: ${fontSize(14)};
  line-height: 1.2;
  text-decoration: none;
  color: ${({ theme }) => theme.accent};
  cursor: pointer;
  transition: all 0.2s ease;

  ${({ theme }) =>
    theme.newIndex &&
    css`
      font-size: ${fontSize(18)};
      text-decoration: underline;
    `};

  :hover,
  :active,
  :focus {
    color: #ffc54c;
    text-decoration: none;
  }
`;

const SignUpTabBlock = styled.div`
  display: block;
  margin-top: 42px;
  font-size: ${fontSize(18)};
  line-height: 1.2;
  font-weight: bold;
  color: ${({ theme }) => theme.mainDarkText};
  text-align: center;
  vertical-align: middle;

  ${({ theme, isDarkPage }) =>
    theme.newIndex &&
    css`
      margin-top: 20px;
      color: ${isDarkPage ? "#ffffff" : "rgba(34, 34, 34, 0.59)"};
    `};
`;

const SignUpTab = styled.span`
  color: ${({ theme }) => (theme.newIndex ? theme.accent : theme.mainLink)};
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => (theme.newIndex ? "#ffffff" : theme.accent)};
  }
`;

const WelcomeText = styled.div`
  margin-bottom: 32px;
  font-size: 38px;
  line-height: 46px;
  font-weight: bold;
  color: #ffffff;
  text-align: center;

  @media (max-width: 479px) {
    margin-bottom: 15px;
    font-size: 28px;
    line-height: 1.2;
  }
`;

type State = {
  pending: "host" | "fb" | "gp" | null,
  submitTimeError: string | React.Node | null,
  responseTimeError: string | null,
};

function isAndroidAppMessagingAvailable() {
  return (
    window.nativeInterface != null &&
    window.nativeInterface.onFacebookLoginClick != null
  );
}

function isIOSAppMessagingAvailable() {
  return (
    window.webkit != null &&
    window.webkit.messageHandlers != null &&
    window.webkit.messageHandlers["nativeInterface"] != null
  );
}

const useNativeInterface = (fnName = "", data) => {
  try {
    if (
      window.nativeInterface != null &&
      typeof window.nativeInterface[fnName] === "function"
    ) {
      window.nativeInterface[fnName](data);
    }
    if (isIOSAppMessagingAvailable()) {
      window.webkit.messageHandlers["nativeInterface"].postMessage(
        JSON.stringify({
          function: fnName,
          param: data,
        })
      );
    }
  } catch (error) {
    console.log(error);
  }
};

class LoginMain extends React.Component<$FlowFixMeProps, State> {
  state = {
    pending: null,
    emailError: null,
    passwordError: null,
    submitTimeError: null,
    responseTimeError: null,
  };

  login = async (event: SyntheticEvent<HTMLButtonElement>) => {
    event.preventDefault();

    // TODO: validate
    const { email, password, locale } = this.props;

    gaReducer("CLICKBTN_LOGIN_MAINPAGE", { userId: "N/A" });

    const submitTimeError =
      validators.emptyFields(email, password) ||
      validators.emailFull(email) ||
      validators.password(password);

    let errors = {};
    errors.emailError =
      validators.emptyFields(email) || validators.emailFull(email);
    errors.passwordError =
      validators.emptyFields(password) || validators.password(password);

    if (submitTimeError) {
      this.setState(errors);
      return;
    }

    this.setState({ pending: "host" });

    const credentials = querystring.stringify({
      email,
      password,
      locale,
    });

    try {
      const response = await this.props.fetch(`/auth-user?${credentials}`, {
        unstable_dontInferResponse: true,
      });

      this.setState({ pending: null });

      switch (response.status) {
        case "OK":
          this.props.dispatch({ type: "REQUEST_PROFILE" });
          submitAppsFlyerParameters();
          // Операции запрашиваются при подключении к сокету,
          // а сокет подключается при логине, т.е. нет смысла запрашивать ещё раз
          // this.props.dispatch({ type: "REQUEST_DEAL_HISTORY" });

          if (!this.props.noRedirect) {
            const query = querystring.parse(
              window.location.search.substring(1)
            );
            if (!query.redirect) {
              this.props.history.push("/trade");
            }
          }
          useNativeInterface("setUserKey", response.user_key);
          break;
        case "ERROR":
          if (response.must_confirm_phone) {
            // this.props.onLeave(PHONE_CONFIRM_TAB_ID);
          } else {
            this.setState({ responseTimeError: response.message });
          }
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(error);
    }
  };

  handleInputChange = (event: SyntheticInputEvent<HTMLInputElement>) => {
    this.setState({
      [`${event.target.name}Error`]: null,
      submitTimeError: null,
      responseTimeError: null,
    });

    this.props.onInputChange(event);
  };

  socialAuthGate = (network: string, event) => {
    //для веб-вью приложения авторизация через FB SDK, которую делает Малышев и редиректит с токеном фб на бэк, где получает наш токен и редиректит на платформу с этим токеном, где авторизуется.
    if (network === "fb") {
      if (isAndroidAppMessagingAvailable()) {
        event.preventDefault();
        window.nativeInterface.onFacebookLoginClick();
      } else if (isIOSAppMessagingAvailable()) {
        event.preventDefault();
        window.webkit.messageHandlers["nativeInterface"].postMessage(
          JSON.stringify({
            function: "onFacebookLoginClick",
          })
        );
      }
    }
    this.setState({ pending: network });
  };

  render() {
    const {
      email,
      password,
      notification,
      notificationType,
      showSocial,
      isDarkPage,
    } = this.props;

    const {
      pending,
      emailError,
      passwordError,
      submitTimeError,
      responseTimeError,
    } = this.state;

    const inputTimeError = validators.email(email);

    const tabs = (
      <TabContainer>
        <Tab
          id={REGISTER_TAB_ID}
          isActive={false}
          onClick={() => this.props.onLeave(REGISTER_TAB_ID)}
        >
          <Gettext>Registration</Gettext>
        </Tab>
        <Tab id={LOGIN_TAB_ID} isActive={true}>
          <Gettext>Log in</Gettext>
        </Tab>
      </TabContainer>
    );

    return (
      <React.Fragment>
        {!isIronForm && tabs}
        <Container>
          <form action="#" method="post">
            <div>
              {isIronForm && (
                <WelcomeText>
                  <Gettext>Welcome back</Gettext>
                </WelcomeText>
              )}
              <FormText>
                <Gettext>Sign in to your account</Gettext>
              </FormText>
            </div>
            <Input
              type="email"
              name="email"
              placeholder={this.props.gettext`Email`}
              id="login-email"
              onChange={this.handleInputChange}
              value={email}
              autocomplete="email"
            />
            {emailError && <ErrorMessage message={emailError} />}

            <input
              className="sp_push_custom_data"
              type="hidden"
              name="Email"
              value={email}
            />

            <Input
              type="password"
              name="password"
              placeholder={this.props.gettext`Password`}
              id="login-pass"
              onChange={this.handleInputChange}
              value={password}
            />

            {passwordError && <ErrorMessage message={passwordError} />}

            {responseTimeError ? (
              <ErrorMessage message={responseTimeError} />
            ) : submitTimeError ? (
              <ErrorMessage message={submitTimeError} />
            ) : notification ? (
              <Message
                notification={notification}
                notificationType={notificationType}
                toggleTab={this.props.onLeave}
              />
            ) : inputTimeError ? (
              <ErrorMessage message={inputTimeError} />
            ) : null}

            <SubmitContainer showSocial={showSocial}>
              <BtnFormSubmit
                id="login"
                onClick={this.login}
                loader={pending === "host"}
              >
                <Gettext>login</Gettext>
              </BtnFormSubmit>{" "}
              {(showSocial || isIronForm) && (
                <React.Fragment>
                  <Text>
                    <Gettext>Or login with</Gettext>
                  </Text>{" "}
                  <SocialLink
                    network="fb"
                    pending={pending === "fb"}
                    href={loginWith("fb", this.props)}
                    onClick={(event) => this.socialAuthGate("fb", event)}
                  />
                  <SocialLink
                    network="gp"
                    pending={pending === "gp"}
                    href={loginWith("gp", this.props)}
                    onClick={(event) => this.socialAuthGate("gp", event)}
                  />
                </React.Fragment>
              )}
            </SubmitContainer>
            <ForgotContainer>
              <ForgotLink
                onClick={() =>
                  this.props.onLeave(PASSWORD_RESTORE_TAB_ID, "email")
                }
              >
                <Gettext>Forgot password?</Gettext>
              </ForgotLink>
            </ForgotContainer>
            {isIronForm && (
              <SignUpTabBlock isDarkPage={isDarkPage}>
                <Gettext>Don't have an account?</Gettext>{" "}
                <SignUpTab
                  id={REGISTER_TAB_ID}
                  isActive={false}
                  onClick={() => this.props.onLeave(REGISTER_TAB_ID)}
                >
                  <Gettext>Sign up</Gettext>
                </SignUpTab>
              </SignUpTabBlock>
            )}
          </form>
        </Container>
      </React.Fragment>
    );
  }
}

export default withGettext(withFetch(withRouter(LoginMain)));



// WEBPACK FOOTER //
// ./src/components/Auth/Tabs/LoginMain.js